import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import SetoresDaUnidade from './SetoresDeUnidade';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { DeletePredio, GetPredio, SetPredio } from '@/modules/structure/predio/predio.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { predioFormSchema } from '@/modules/structure/predio/predio.schema';
import { FieldError, formControlClass, formSelectClass } from '@/shared/components/forms/FormFieldFeedback';
import { numberInputValueAs } from '@/shared/utils/formZod.utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ReadSetorDto, CreatePredioDto, UpdatePredioDto, ReadComarcaDto, ReadPredioDto } from '@/api.types';
import { SubmitHandler, useForm, type Resolver } from 'react-hook-form';
import { Tabelas } from '@/shared/constants/tables';
import { readComarca } from '@/modules/structure/comarca/comarca.service';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';

export default function PredioEditPage() {
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const params = useParams();

  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Predio_Criar, onClick: () => {} },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  const [loaded, setLoaded] = useState(false);
  const [comarcas, setComarcas] = useState<ReadComarcaDto[]>([]);
  const [setores, setSetores] = useState<ReadSetorDto[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ReadPredioDto>({
    resolver: zodResolver(predioFormSchema) as unknown as Resolver<ReadPredioDto>,
    mode: 'onTouched',
    defaultValues: {
      ordem: 0,
      nome: '',
      comarca: undefined,
      endereco: {
        cep: '',
        cidade: '',
        bairro: '',
        logradouro: '',
        complemento: '',
        numero: undefined,
      },
      telefoneprincipal: {
        ddd: undefined,
        numero: '',
      },
      telefonesecundario: {
        ddd: undefined,
        numero: '',
      },
    },
  });

  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');

  useEffect(() => {
    Promise.all([
      readComarca({ skip: 0, take: 1000 })
        .then((result) => {
          setComarcas(result[0]);
        })
        .catch((res) => {
          toast.error((res as string) + '');
        }),
      id > 0
        ? GetPredio(id)
          .then((result) => {
            if (result) {
              reset(result);
              setSetores((result.setores as ReadSetorDto[]) || []);
            }
          })
          .catch((res) => {
            toast.error((res as string) + '');
          })
        : Promise.resolve(),
    ]).then(() => setLoaded(true));
  }, [id, reset]);

  const onSubmit: SubmitHandler<ReadPredioDto> = (data) => {
    setIsLoading(true);

    const dataToSave: CreatePredioDto | UpdatePredioDto = {
      id: id > 0 ? id : undefined,
      ordem: Number(data.ordem) || 0,
      comarca: { id: Number(data.comarca?.id) },
      nome: data.nome || '',
      endereco: {
        cep: data.endereco?.cep,
        cidade: data.endereco?.cidade,
        bairro: data.endereco?.bairro,
        logradouro: data.endereco?.logradouro,
        complemento: data.endereco?.complemento,
        numero: data.endereco?.numero ? Number(data.endereco.numero) : undefined,
      },
      telefoneprincipal: {
        ddd: data.telefoneprincipal?.ddd ? Number(data.telefoneprincipal.ddd) : undefined,
        numero: data.telefoneprincipal?.numero,
      },
      telefonesecundario: {
        ddd: data.telefonesecundario?.ddd ? Number(data.telefonesecundario.ddd) : undefined,
        numero: data.telefonesecundario?.numero,
      },
      setores: setores.map((s) => ({
        id: s.id,
        sigla: s.sigla,
        nome: s.nome,
        capacidade: s.capacidade,
        agrupar: s.agrupar,
        responsavel: s.responsavel,
      })),
    };

    SetPredio(dataToSave)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Predio_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setModalExcluirShow(false);
    DeletePredio(id)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Predio_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  };

  const handleRegistros = (lista: ReadSetorDto[]) => {
    setSetores(lista);
  };

  const onInvalid = () => {
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Unidade</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit, onInvalid)(event);
        }}
      >
        <div className="row">
          <div className="mb-3 col-md-1">
            <label htmlFor="ordem" className="form-label">
              Ordem: <span className="text-danger">*</span>
            </label>
            <input
              id="ordem"
              type="number"
              title="Ordem no relatório"
              className={formControlClass(!!errors.ordem)}
              min={0}
              {...register('ordem', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.ordem?.message} />
          </div>

          <div className="col-md-5">
            <label htmlFor="comarca" className="form-label">
              Comarca: <span className="text-danger">*</span>
            </label>
            <select
              id="comarca"
              title="Comarca"
              className={formSelectClass(!!errors.comarca?.id)}
              {...register('comarca.id', { setValueAs: numberInputValueAs })}
            >
              <option value="">Selecione uma comarca</option>
              {comarcas.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
            <FieldError message={errors.comarca?.id?.message} />
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="nome" className="form-label">
              Nome: <span className="text-danger">*</span>
            </label>
            <input
              id="nome"
              title="Nome"
              type="text"
              className={formControlClass(!!errors.nome)}
              {...register('nome')}
            />
            <FieldError message={errors.nome?.message} />
          </div>

          <div className="col-md-8 p-3">
            <div className="card">
              <div className="card-header">
                <span>Endereço</span>
              </div>
              <div className="card-body row">
                <div className="col-md-4">
                  <label htmlFor="cep" className="form-label">
                    CEP:
                  </label>
                  <input
                    id="cep"
                    title="CEP"
                    type="text"
                    className="form-control"
                    maxLength={8}
                    {...register('endereco.cep')}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="cidade" className="form-label">
                    Cidade:
                  </label>
                  <input
                    id="cidade"
                    title="Cidade"
                    type="text"
                    className="form-control"
                    {...register('endereco.cidade')}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="bairro" className="form-label">
                    Bairro:
                  </label>
                  <input
                    id="bairro"
                    title="Bairro"
                    type="text"
                    className="form-control"
                    {...register('endereco.bairro')}
                  />
                </div>
                <div className="col-md-5 mt-3">
                  <label htmlFor="logradouro" className="form-label">
                    Logradouro:
                  </label>
                  <input
                    id="logradouro"
                    title="Logradouro"
                    type="text"
                    className="form-control"
                    {...register('endereco.logradouro')}
                  />
                </div>
                <div className="col-md-5 mt-3">
                  <label htmlFor="complemento" className="form-label">
                    Complemento:
                  </label>
                  <input
                    id="complemento"
                    title="Complemento"
                    type="text"
                    className="form-control"
                    {...register('endereco.complemento')}
                  />
                </div>
                <div className="col-md-2 mt-3">
                  <label htmlFor="numero" className="form-label">
                    Número:
                  </label>
                  <input
                    id="numero"
                    title="Número"
                    type="text"
                    className="form-control"
                    {...register('endereco.numero', { setValueAs: numberInputValueAs })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4 p-3">
            <div className="card">
              <div className="card-header">
                <span>Telefones</span>
              </div>
              <div className="card-body row">
                <div className="col-md-4">
                  <label htmlFor="ddd1" className="form-label">
                    DDD: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ddd1"
                    type="number"
                    className={formControlClass(!!errors.telefoneprincipal?.ddd)}
                    maxLength={3}
                    min={0}
                    max={999}
                    {...register('telefoneprincipal.ddd', { setValueAs: numberInputValueAs })}
                  />
                  <FieldError message={errors.telefoneprincipal?.ddd?.message} />
                </div>
                <div className="col-md-8 mb-3">
                  <label htmlFor="fone1" className="form-label">
                    Telefone:
                  </label>
                  <input
                    id="fone1"
                    type="text"
                    className={formControlClass(!!errors.telefoneprincipal?.numero)}
                    maxLength={10}
                    {...register('telefoneprincipal.numero')}
                  />
                  <FieldError message={errors.telefoneprincipal?.numero?.message} />
                </div>

                <div className="col-md-4">
                  <label htmlFor="ddd2" className="form-label">
                    DDD: <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ddd2"
                    type="number"
                    className={formControlClass(!!errors.telefonesecundario?.ddd)}
                    maxLength={3}
                    min={0}
                    max={999}
                    {...register('telefonesecundario.ddd', { setValueAs: numberInputValueAs })}
                  />
                  <FieldError message={errors.telefonesecundario?.ddd?.message} />
                </div>
                <div className="col-md-8">
                  <label htmlFor="fone2" className="form-label">
                    Telefone:
                  </label>
                  <input
                    id="fone2"
                    type="text"
                    className={formControlClass(!!errors.telefonesecundario?.numero)}
                    maxLength={10}
                    {...register('telefonesecundario.numero')}
                  />
                  <FieldError message={errors.telefonesecundario?.numero?.message} />
                </div>
              </div>
            </div>
          </div>
          <div>
            <br />
            <hr />
          </div>
          {loaded && (
            <SetoresDaUnidade
              unidadeId={id}
              updateRegistros={handleRegistros}
            />
          )}

          <EditadoPorRodape
            dados={{
              editadoPor: editadoPor ? { username: editadoPor.username } : undefined,
              editadoEm: editadoEm,
            }}
          />
          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      />
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.predio}
        idRegistro={id}
      />
    </>
  );
}
